/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import type { JSX } from "react";

import { SystemDialogContent } from "@cms/components/data/Dialog";
import { SystemTextInput } from "@cms/components/data/TextInput";
import { useDialog } from "@cms/providers/DialogProvider";
import { useSnackbar } from "@cms/providers/SnackbarProvider";
import { isValidUrl } from "@dl/declarations";
import { $createParagraphNode, $getSelection, $insertNodes, $isRangeSelection, LexicalEditor } from "lexical";
import { useEffect, useRef, useState } from "react";
import { $createOEmbedNode } from "../../nodes/OEmbedNode";

function getOEmbedEndpoint(url: string): string | null {
  if (["youtube.com", "youtu.be"].some((domain) => url.includes(domain)))
    return "https://www.youtube.com/oembed";
  if (["twitter.com", "x.com"].some((domain) => url.includes(domain)))
    return "https://publish.twitter.com/oembed";
  if (url.includes("instagram.com"))
    return "https://graph.facebook.com/v8.0/instagram_oembed";
  if (url.includes("tiktok.com"))
    return "https://www.tiktok.com/oembed";

  return null;
}

function detectSubType(url: string): string | null {
  if (["youtube.com", "youtu.be"].some((domain) => url.includes(domain))) return "youtube";
  if (["twitter.com", "x.com"].some((domain) => url.includes(domain))) return "twitter";
  if (url.includes("instagram.com")) return "instagram";
  if (url.includes("tiktok.com")) return "tiktok";

  return null;
}

export function InsertSocialUrlDialog({
  activeEditor,
}: {
  activeEditor: LexicalEditor;
}): JSX.Element {
  const { closeDialog } = useDialog();
  const { showSnackbar } = useSnackbar();

  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState('');

  const onClick = async () => {
    if (!url) {
      showSnackbar('URL is required. Please enter a URL.', 'error');

      return
    }

    const isValid = isValidUrl(url);

    if (!isValid) {
      showSnackbar('Invalid URL. Please enter a valid URL.', 'error');

      return;
    }

    const subtype = detectSubType(url);

    if (!subtype) {
      showSnackbar('Unsupported URL. The provided URL is not supported for embedding.', 'error');

      return;
    }

    const oEmbedUrl = getOEmbedEndpoint(url);

    if (!oEmbedUrl) {
      showSnackbar('Unsupported URL. The provided URL is not supported for embedding.', 'error');

      return;
    }

    const response = await fetch(`${oEmbedUrl}?url=${encodeURIComponent(url)}&format=json`);
    const data: { html: string } = await response.json();

    activeEditor.update(() => {
      const selection = $getSelection();

      if (
        selection &&
        $isRangeSelection(selection)
        && selection.isCollapsed()
      ) {
        const node = $createOEmbedNode(url, data.html, subtype);
        selection.insertNodes([node, $createParagraphNode()]);
      } else {
        // Fallback: insert at the end of the document
        const node = $createOEmbedNode(url, data.html, subtype);
        $insertNodes([node, $createParagraphNode()]);
      }

      closeDialog();
    });
  };

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  return (
    <SystemDialogContent
      dialogProps={{
        title: 'Insert Social URL',
        maxWidth: 'md'
      }}
      content={{
        component: (
          <>
            <SystemTextInput
              autoFocus
              name="cs-social-url"
              placeholder={'Enter Social URL'}
              onChange={(e) => setUrl(e.target.value)}
              value={url}
              inputSize="full"
              inputRef={inputRef}
            />
          </>
        )
      }}
      actionsProps={{
        submit: {
          onClick: onClick
        },
        cancel: {
          onClick: closeDialog
        }
      }}
    />
  )
}
